import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { FORECASTLY_LOGO_LIGHT, PROFITMIND_LOGO } from "@/lib/brand";
import { toast } from "sonner";
import { ArrowRight } from "@phosphor-icons/react";

// Sign-in only. Account creation happens server-side inside /auth/finish after the
// buyer completes a Stripe Checkout from the Landing page. There is no free-signup
// path inside the app any more.
export default function Auth() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();

  // Email CTAs (e.g. the monthly nudge) link to /auth?next=/actuals so we can land
  // the user on the intended page right after sign-in instead of /dashboard.
  const nextPath = (() => {
    const raw = params.get("next") || "";
    // Only allow internal paths — never an external redirect target.
    return raw.startsWith("/") && !raw.startsWith("//") ? raw : "/dashboard";
  })();

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login({ email: form.email, password: form.password });
      toast.success("Hello again.");
      nav(nextPath);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something's not right. Try again.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="border-b border-black/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-28">
          <a href={process.env.REACT_APP_MARKETING_URL || "https://forecastly.me"} className="flex items-center" data-testid="auth-logo">
            <img
              src={FORECASTLY_LOGO_LIGHT}
              alt="forecastly"
              className="w-[250px] h-auto object-contain"
            />
          </a>
          <a href={process.env.REACT_APP_MARKETING_URL || "https://forecastly.me"} className="btn-secondary">
            Back
          </a>
        </div>
      </header>

      <main className="flex-1 grid md:grid-cols-2">
        <section className="hidden md:flex flex-col p-12 lg:p-20 bg-[#FFBD4A] border-r border-black/15 relative">
          <div className="flex-1 flex flex-col justify-center">
            <div className="section-eyebrow">Welcome back</div>
            <h1 className="font-heading uppercase text-5xl lg:text-6xl tracking-wide mt-6 leading-[1.02]">
              Your forecast, <br />
              still waiting for you.
            </h1>
            <p className="mt-6 text-lg max-w-md text-black/85">
              Sign in to pick up where you left off — your 12-month map, your actuals, your
              insights.
            </p>
          </div>
          {/* Anchored to the bottom of the yellow section (matches the small legal print
              on the right column). */}
          <div className="absolute bottom-6 left-12 lg:left-20 right-12 flex items-center gap-3">
            <img src={PROFITMIND_LOGO} alt="ProfitMIND" className="h-12 w-auto rounded-lg" />
            <div className="text-xs text-black/75 leading-snug">
              <div>forecastly is a brand of ProfitMIND LTD.</div>
              <div>© {new Date().getFullYear()} ProfitMIND. All rights reserved.</div>
            </div>
          </div>
        </section>

        <section className="flex items-center justify-center p-6 md:p-12">
          <div className="w-full max-w-md">
            <div className="section-eyebrow">Sign in</div>
            <h2 className="font-heading uppercase text-4xl tracking-wide mt-3">
              Welcome back.
            </h2>
            <p className="text-black/70 mt-2">
              Let's pick up where you left off.
            </p>

            <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="auth-form">
              <div>
                <label className="block text-sm font-semibold mb-1">Email</label>
                <input
                  type="email"
                  className="input"
                  data-testid="auth-email-input"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Password</label>
                <input
                  type="password"
                  className="input"
                  data-testid="auth-password-input"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                  minLength={6}
                  placeholder="At least 6 characters"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="auth-submit-btn"
                className="btn-primary w-full justify-center"
              >
                {busy ? "One moment…" : "Sign in"} <ArrowRight size={18} weight="bold" />
              </button>
            </form>

            <div className="mt-5 text-sm">
              <Link
                to="/auth/forgot"
                className="underline font-semibold"
                data-testid="auth-forgot-link"
              >
                Forgot your password?
              </Link>
            </div>

            <div
              className="mt-8 p-5 rounded-2xl border-[1.5px] border-black/40 bg-[#FFF7E5]"
              data-testid="auth-new-customer-card"
            >
              <div className="font-heading uppercase text-lg tracking-wide">New here?</div>
              <p className="mt-2 text-sm text-black/80 leading-relaxed">
                Accounts are created at checkout. Pick a plan on forecastly.me — you'll set
                your password right after payment.
              </p>
              <a
                href={(process.env.REACT_APP_MARKETING_URL || "https://forecastly.me") + "/pricing"}
                className="btn-primary mt-4 inline-flex"
                data-testid="auth-pick-plan-cta"
              >
                See plans <ArrowRight size={16} weight="bold" />
              </a>
            </div>

            <p className="text-xs text-black/60 mt-6 leading-relaxed">
              By continuing you agree to our{" "}
              <a
                href="https://profitmind.co.uk/terms-and-conditions"
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-semibold"
                data-testid="auth-terms-link"
              >
                terms and conditions
              </a>
              . This is your private forecasting space. No spam. No selling of your data. For more
              details, please read our{" "}
              <a
                href="https://profitmind.co.uk/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-semibold"
                data-testid="auth-privacy-link"
              >
                Privacy Policy
              </a>
              .
            </p>
          </div>
        </section>
      </main>
    </div>
  );
}
